
import * as React from 'react';
import EditorProps from '../../components/editorinterface';
import * as Autosuggest from 'react-autosuggest';
import { Tag } from '../../store/types';
import './index.css';

interface Props extends EditorProps {

};

class SelectEditor extends React.Component<Props, SelectEditorState> {

  constructor(props: Props) {

    super(props);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.enterEditMode = this.enterEditMode.bind(this);
    this.exitEditMode = this.exitEditMode.bind(this);
    this.addNewSuggestion = this.addNewSuggestion.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.state = {
      showAutoSuggest: false,
      value: '',
      suggestions: props.field.enumColors!.values,
    };
  }

  render() {
    const state = this.state;
    const props = this.props;
    const className = (state.showAutoSuggest) ?
      "form-control select-editor select-editor-active"
      :
      "form-control select-editor";

    const inputProps = {
      placeholder: 'Find an option',
      value: state.value,
      onChange: this.onInputChange,
      autoFocus: true,
      onBlur: this.exitEditMode,
      onKeyDown: this.onKeyDown,
    };

    const bgColor = this.getColorOfValue(props.value);
    const textColor = this.getTextColor(props.value);
    return (
      <div>
        <div onClick={this.enterEditMode} className={className} onFocus={this.enterEditMode} tabIndex={0}>
          <span
            style={{ background: `${bgColor}`, color: `${textColor}` }}
            className="option-style"
          >
            {props.value}
          </span>
        </div>
        {(state.showAutoSuggest) ?
          <Autosuggest
            suggestions={state.suggestions}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
            alwaysRenderSuggestions={true}
            // Not needed, we control the rendering of the whole component
            // Setting it to true lets onSuggestionsClearRequested become unneded for that matter
            onSuggestionSelected={this.onSuggestionSelected}
            getSuggestionValue={getSuggestionValue}
            shouldRenderSuggestions={this.alwaysRenderSuggestions}
            renderSuggestion={this.renderSuggestion}
            highlightFirstSuggestion={true}
            inputProps={inputProps}
          /> : null}
        {this.addNewValue()}
      </div>
    );
  }

  private enterEditMode() {
    if (!this.state.showAutoSuggest && !this.props.field.readOnly)
      this.setState({ showAutoSuggest: true });
  }

  private exitEditMode() {
    if (this.state.showAutoSuggest)
      this.setState({ showAutoSuggest: false });
  }

  private onInputChange(event: any) {
    const value = (typeof event.target.value === 'string') ? event.target.value : '';
    this.setState({ value });
  }

  private addNewSuggestion(event?: any) {
    const value = this.state.value;
    this.exitEditMode();
    this.props.onChange(this.props.field.id, value);
  }

  private onSuggestionSelected(event: any, { suggestionValue }: any) {
    const value = (suggestionValue !== '') ? suggestionValue : null;
    this.exitEditMode();
    this.props.onChange(this.props.field.id, value);
  }

  private getColorOfValue(value: string) {
    const selectedSuggestion = this.props.field.enumColors!.values.find(
      element => element.value === value);
    return (selectedSuggestion && !!this.props.field.enumColors!.displayColors) ?
      selectedSuggestion.color : '#FFFFFF00';
  }

  private alwaysRenderSuggestions() {
    // Render suggestions when input is empty
    return true;
  }

  private onSuggestionsFetchRequested = ({ value }: any) => {
    this.setState({ suggestions: this.getSuggestions(value) });
  };

  private getSuggestions(value: string) {
    const emptySuggestion: Tag = { value: '', color: '#FFF' };
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    return (inputLength === 0) ?
      [emptySuggestion].concat(this.props.field.enumColors!.values)
      :
      this.props.field.enumColors!.values.filter(element =>
        element.value.toLowerCase().slice(0, inputLength) === inputValue
      );
  }

  // Use your imagination to render suggestions.
  private renderSuggestion(suggestion: Tag) {
    const enumColors = this.props.field.enumColors;
    const displayColors = (!!enumColors!.displayColors && suggestion.value !== '')
    const bgColor = (displayColors) ? suggestion.color : '#ffffff00';
    const textColor = this.getTextColor(suggestion.value);
    return (
      <div className="react-autosuggest__suggestion">
        <span
          style={{ background: `${bgColor}`, color: `${textColor}` }}
          className="option-style"
        >
          {suggestion.value}
        </span>
      </div>
    );
  }

  private addNewValue() {
    const suggestionLength = this.state.suggestions.length;
    const strict = !!this.props.field.enumColors!.strict;
    const autoSuggestIsOpen = this.state.showAutoSuggest;
    return (
      (suggestionLength === 0 && !strict && autoSuggestIsOpen) ?
        <div className="react-autosuggest__container">
          <div onMouseDown={this.addNewSuggestion} className="add-new-value">
            <p>Create a new option named <b>{this.state.value}</b></p>
          </div>
        </div>
        : null);
  }

  private onKeyDown(e: any) {
    // Enter key pressed and we can add a new suggestion
    const suggestionLength = this.state.suggestions.length;
    const strict = !!this.props.field.enumColors!.strict;
    const autoSuggestIsOpen = this.state.showAutoSuggest;

    if (e.keyCode == 13 && suggestionLength === 0 && !strict && autoSuggestIsOpen)
        this.addNewSuggestion();

    // Escape key pressed : clear input if any, else : exit edit mode
    if (e.keyCode == 27 && this.state.value !== '')
        this.setState({value: ''});
    else if (e.keyCode == 13 || e.keyCode == 27 || e.keyCode == 9) {// enter, escape or tab
      this.exitEditMode();
    }
  }

  private getTextColor(value: string) {
      let color = colorIsLight(this.getColorOfValue(value)) ? "#000" : "#FFF";
      const selectedSuggestion = this.props.field.enumColors!.values.find(
        element => element.value === value);
      if (selectedSuggestion && selectedSuggestion.colortext)
        color = selectedSuggestion.colortext;
      return color;
  }
}

const getSuggestionValue = (suggestion: Tag) => suggestion.value;

interface SelectEditorState {
  value: string;
  showAutoSuggest: boolean;
  suggestions: Array<Tag>;
}

const colorIsLight = (colorHex: string | null | undefined) => {
	var a;
	if (colorHex) {
        var red = parseInt(colorHex.substr(1,2),16);
        var green = parseInt(colorHex.substr(3,2),16);
        var blue = parseInt(colorHex.substr(5,2),16);
		a = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
	}
	else{
		a = 1;
    }
    return (a > 0.73);
}

export default SelectEditor;



// WEBPACK FOOTER //
// ./src/components/selecteditor/index.tsx